import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { paisa, type Money } from '@fleet/shared';
import {
  useAvailableVehiclesMutation,
  useBookingQuery,
  useConvertEnquiryMutation,
  useCreateBookingMutation,
  useCreateCustomerMutation,
  useCustomersQuery,
  useDriversQuery,
  useEnquiryQuery,
  useQuoteMutation,
  useUpdateBookingMutation,
  useUpdateCustomerMutation,
} from '@/api/endpoints';
import type { TripArea, Vehicle } from '@/api/types';
import { Button, LinkButton } from '@/design/Button';
import { MoneyText } from '@/design/Data';
import { ErrorState } from '@/design/EmptyState';
import {
  Checkbox,
  DateTimeInput,
  Field,
  FieldRow,
  MoneyInput,
  PhoneInput,
  SelectInput,
  TextInput,
} from '@/design/Field';
import { Panel, PanelBody } from '@/design/Panel';
import { useToast } from '@/design/Toast';
import { TRIP_AREA_LABELS, distinctVehicleNames, optionsFrom } from '@/features/fleet/labels';
import { PaymentModal, type PayableBooking } from '@/features/money/PaymentModal';
import { readApiError } from '@/lib/apiError';
import { checkLicence, checkName, checkPhone, checkWindow } from '@/lib/validate';
import { fromInputValue, suggestedEnd, suggestedStart, useTime } from '@/lib/time';
import { BOOKING_SOURCES, FUEL_POLICIES } from './status';

/**
 * What each field is called, for the summary beside the button.
 *
 * QA converted an enquiry with a field missing and got "This booking is not
 * saved. Check the field above" — which field was left to them to find. The
 * summary names them now, and it has to cover every field the API can refuse,
 * not just the handful somebody remembered: an unlisted one falls through to
 * its raw name and the reader sees `licence_no`, which is how the message
 * stopped being readable in the first place.
 */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  area: 'Area',
  category: 'Category',
  customer: 'Customer',
  destination: 'Going to',
  discount: 'Discount',
  driver: 'Driver',
  end_at: 'Back',
  fuel_policy: 'Fuel',
  licence_no: 'Licence number',
  name: 'Name',
  notes: 'Notes',
  phone: 'Phone number',
  source: 'Source',
  start_at: 'Out',
  subtotal: 'Subtotal',
  total: 'Total',
  vehicle: 'Vehicle',
};

/**
 * Taking a booking.
 *
 * Built for the 60-second target: the dates come first, because everything else
 * depends on them; the vehicle list then shows *only* what is actually free for
 * that window, so a staff member cannot promise a car that is out; and the
 * price is fetched from the rate cards with one press, still editable, because
 * the person on the phone is the one negotiating.
 */
export function BookingFormPage(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== undefined && id !== 'new';
  const [params] = useSearchParams();
  const fromEnquiry = params.get('enquiry');
  const navigate = useNavigate();
  const { toInputValue } = useTime();

  const existing = useBookingQuery(id ?? '', { skip: !isEditing });
  const enquiry = useEnquiryQuery(fromEnquiry ?? '', { skip: fromEnquiry === null });
  const drivers = useDriversQuery();

  const [createBooking, creating] = useCreateBookingMutation();
  const [updateBooking, updating] = useUpdateBookingMutation();
  const [convertEnquiry, converting] = useConvertEnquiryMutation();
  const [createCustomer, creatingCustomer] = useCreateCustomerMutation();
  const [updateCustomer, updatingCustomer] = useUpdateCustomerMutation();
  const [findVehicles, availability] = useAvailableVehiclesMutation();
  const [askQuote, quoting] = useQuoteMutation();

  // --- Customer -------------------------------------------------------------
  const [customerSearch, setCustomerSearch] = useState('');
  // Two characters, not three: a phone search wants digits but a name search
  // is often two letters in before the useful matches appear, and "Om" is a
  // whole first name.
  const searching = customerSearch.trim().length >= 2;
  /**
   * Whether the customer box currently has the attention of the person typing.
   *
   * The suggestions used to be shown purely on "is there enough text to
   * search", which meant they were still hanging over the form after focus had
   * moved on to the phone number — a menu sitting on top of the field being
   * filled in. A list of suggestions is only ever an answer to the box it
   * belongs to, so it lives and dies with that box.
   */
  const [browsing, setBrowsing] = useState(false);
  const customers = useCustomersQuery(searching ? { q: customerSearch.trim() } : undefined);
  const matches = customers.data?.results ?? [];
  const [customerId, setCustomerId] = useState('');
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [licenceNo, setLicenceNo] = useState('');

  /** The row behind `customerId`, so the form can show who was chosen. */
  const chosenCustomer = matches.find((candidate) => candidate.id === customerId);

  /** On screen only while the box is in use and has something to offer. */
  const showSuggestions = browsing && searching && matches.length > 0;

  // --- The booking ----------------------------------------------------------
  const [startAt, setStartAt] = useState(suggestedStart);
  const [endAt, setEndAt] = useState(suggestedEnd);
  const [vehicleId, setVehicleId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [withDriver, setWithDriver] = useState(false);
  const [licenceToFollow, setLicenceToFollow] = useState(false);
  const [fuelPolicy, setFuelPolicy] = useState<string>('same_to_same');
  const [source, setSource] = useState<string>('phone');
  const [area, setArea] = useState<TripArea>('inside_valley');
  const [destination, setDestination] = useState('');
  const [subtotal, setSubtotal] = useState<Money>(paisa(0));
  const [discount, setDiscount] = useState<Money>(paisa(0));
  const [notes, setNotes] = useState('');
  const [explanation, setExplanation] = useState('');
  /** The booking just taken, waiting for its advance. See `offerToTakePayment`. */
  const [takePaymentFor, setTakePaymentFor] = useState<PayableBooking | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const toast = useToast();

  const total = paisa(Math.max(0, subtotal - discount));

  useEffect(() => {
    const booking = existing.data;
    if (booking === undefined) return;
    setCustomerId(booking.customer);
    setStartAt(toInputValue(booking.start_at as never));
    setEndAt(toInputValue(booking.end_at as never));
    setVehicleId(booking.vehicle ?? '');
    setDriverId(booking.driver ?? '');
    setWithDriver(booking.with_driver ?? false);
    setLicenceToFollow(booking.licence_to_follow ?? false);
    setFuelPolicy(booking.fuel_policy ?? 'same_to_same');
    setSource(booking.source ?? 'phone');
    setArea(booking.area ?? 'inside_valley');
    setDestination(booking.destination ?? '');
    setSubtotal(paisa(booking.subtotal ?? 0));
    setDiscount(paisa(booking.discount ?? 0));
    setNotes(booking.notes ?? '');
    setLicenceNo(booking.customer_licence_no ?? '');
  }, [existing.data, toInputValue]);

  useEffect(() => {
    if (enquiry.data === undefined) return;
    setNewName(enquiry.data.name);
    setNewPhone(enquiry.data.phone);
    setNotes(enquiry.data.message ?? '');
    if (enquiry.data.wanted_from) setStartAt(toInputValue(enquiry.data.wanted_from as never));
    if (enquiry.data.wanted_to) setEndAt(toInputValue(enquiry.data.wanted_to as never));
  }, [enquiry.data, toInputValue]);

  const saving = creating.isLoading || updating.isLoading || converting.isLoading;
  const problems = readApiError(
    creating.error ??
      updating.error ??
      converting.error ??
      creatingCustomer.error ??
      updatingCustomer.error,
  );

  /**
   * Errors this form worked out for itself, before anything was sent.
   *
   * Separate from `problems`, which is what the server said. Both end up under
   * the same field through `errorFor`, because from the counter's side of the
   * screen there is no difference — something is wrong with this box.
   */
  const [missing, setMissing] = useState<Readonly<Record<string, string>>>({});

  /**
   * What is wrong with a field right now.
   *
   * Three sources, in the order they become true: what the browser can see as
   * it is typed, what the form worked out when Save was pressed, and what the
   * server said. A field only complains once it has something in it —
   * shouting "enter a name" at an empty form nobody has touched yet is noise,
   * and `requiredGaps` covers the empty case at the moment it matters.
   */
  const liveProblems: Readonly<Record<string, string | undefined>> = {
    name: customerSearch.trim() === '' ? undefined : checkName(customerSearch),
    phone: newPhone.trim() === '' ? undefined : checkPhone(newPhone),
    licence_no: checkLicence(licenceNo),
    end_at: checkWindow(startAt, endAt),
  };

  const errorFor = (field: string): string | undefined =>
    liveProblems[field] ?? missing[field] ?? problems.fields[field];

  /** Nothing the browser can already see is wrong. The server still decides. */
  const looksRight =
    Object.values(liveProblems).every((problem) => problem === undefined) &&
    Object.keys(requiredGaps()).length === 0;

  const fieldProblems = Object.entries({ ...problems.fields, ...missing }).map(
    ([field, message]) => {
      const label = FIELD_LABELS[field] ?? field;
      const said =
        field === 'customer' && /may not be null|required/i.test(message)
          ? 'nobody has been chosen.'
          : message;
      return `${label}: ${said}`;
    },
  );

  /**
   * Which vehicles are free, asked whenever the answer could have changed.
   *
   * This used to be a button. The commonest way to use the screen was to fill
   * everything in, never press it, and find an empty dropdown — so the form
   * asks for itself. Debounced, because the dates are typed a character at a
   * time and each keystroke would otherwise be a request.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      void findVehicles({
        start_at: fromInputValue(startAt),
        end_at: fromInputValue(endAt),
        area,
      });
    }, 400);
    return () => {
      clearTimeout(timer);
    };
  }, [startAt, endAt, area, findVehicles]);

  async function fetchQuote(): Promise<void> {
    const result = await askQuote({
      vehicle: vehicleId === '' ? null : vehicleId,
      start_at: fromInputValue(startAt),
      end_at: fromInputValue(endAt),
      with_driver: withDriver,
      area,
    }).unwrap();
    setSubtotal(paisa(result.subtotal));
    setExplanation(result.explanation);
  }

  /**
   * Saving, and saying so.
   *
   * This used to fail silently. The API refused the booking — most often
   * because no customer had been chosen — and the only sign of it was DRF's
   * "This field may not be null" rendered next to a field eight hundred pixels
   * above the button that had just been pressed. From the counter's side of the
   * screen the button did nothing, twice, and then there were two bookings.
   *
   * Three things fix that: a check for the one mistake that is easy to make
   * before the request goes at all, a toast on the way out, and — when the
   * server does refuse — scrolling the reason into view instead of leaving it
   * off-screen.
   */
  /**
   * What is empty that cannot be, keyed by field.
   *
   * Only requiredness lives here — whether a phone number is a *real* phone
   * number is the server's rule, and copying it into the browser would give us
   * two rules to keep in step and one of them wrong. What the browser can
   * answer without asking is "this box is empty", and that is the whole of
   * what QA reported: fields left blank produced no message at all, so the
   * button looked broken rather than the form looking incomplete.
   */
  function requiredGaps(): Record<string, string> {
    const gaps: Record<string, string> = {};

    if (vehicleId === '') gaps['vehicle'] = 'Choose a vehicle.';
    // Self-drive means somebody is driving away in one of these cars.
    if (!withDriver && !licenceToFollow && licenceNo.trim() === '') {
      gaps['licence_no'] = 'A self-drive booking needs their licence number.';
    }

    // The enquiry already carries the name and the phone, so there is nothing
    // to fill in and nowhere to put a message about them.
    if (fromEnquiry === null && customerId === '') {
      if (newName.trim() === '') gaps['name'] = 'Enter a name.';
      if (newPhone.trim() === '') gaps['phone'] = 'Enter a phone number.';
    }

    return gaps;
  }

  async function save(): Promise<void> {
    setMissing({});

    // Converting an enquiry is its own endpoint: it creates the customer, links
    // the enquiry to what it became, and prices the trip in one transaction.
    if (fromEnquiry !== null && !isEditing) {
      const gaps = requiredGaps();
      if (Object.keys(gaps).length > 0) {
        setMissing(gaps);
        focusFirstProblem();
        return;
      }
      try {
        // Everything the form collected, not half of it.
        //
        // This used to send the dates, the vehicle and the area, and silently
        // drop the licence, the driver and the category. Two bugs came out of
        // that one omission: a self-drive conversion was refused for a missing
        // licence *that had been typed in*, so retrying could never work and
        // the only way forward looked like reloading the page; and choosing a
        // driver assigned nobody, so the booking existed with no driver on it.
        const booking = await convertEnquiry({
          id: fromEnquiry,
          vehicle: vehicleId,
          driver: driverId === '' ? null : driverId,
          licence_no: licenceNo,
          start_at: fromInputValue(startAt),
          end_at: fromInputValue(endAt),
          with_driver: withDriver,
          area,
          destination,
          // The money and the details, which this call used to drop on the
          // floor. `total` is not sent: the server derives it from these two,
          // so there is no third number free to disagree with them.
          subtotal,
          discount,
          fuel_policy: fuelPolicy as never,
          notes,
        }).unwrap();
        toast.done(`Booking ${booking.reference} taken for ${booking.customer_name}.`);
        offerToTakePayment(booking);
      } catch {
        refused();
      }
      return;
    }

    const gaps = requiredGaps();
    if (Object.keys(gaps).length > 0) {
      setMissing(gaps);
      focusFirstProblem();
      return;
    }

    let customer = customerId;
    if (customer === '' && newName.trim() !== '' && newPhone.trim() !== '') {
      try {
        const created = await createCustomer({
          name: newName,
          phone: newPhone,
          licence_no: licenceNo,
        }).unwrap();
        customer = created.id;
      } catch {
        refused();
        return;
      }
    } else if (
      customer !== '' &&
      !withDriver &&
      licenceNo.trim() !== (chosenCustomer?.licence_no ?? '')
    ) {
      // Typed against an existing customer, so it belongs on their record —
      // that is what makes the same licence recognisable next time, and what
      // a blacklisting is matched on.
      try {
        await updateCustomer({ id: customer, body: { licence_no: licenceNo } }).unwrap();
      } catch {
        refused();
        return;
      }
    }

    const body = {
      customer,
      vehicle: vehicleId,
      driver: driverId === '' ? null : driverId,
      start_at: fromInputValue(startAt),
      end_at: fromInputValue(endAt),
      area,
      destination,
      with_driver: withDriver,
      licence_to_follow: licenceToFollow,
      fuel_policy: fuelPolicy as never,
      source: source as never,
      subtotal,
      discount,
      total,
      notes,
    };

    try {
      const booking = isEditing
        ? await updateBooking({ id, body }).unwrap()
        : await createBooking(body).unwrap();

      toast.done(
        isEditing
          ? `Booking ${booking.reference} saved.`
          : `Booking ${booking.reference} taken for ${booking.customer_name}.`,
      );
      if (isEditing) {
        navigate('/bookings');
        return;
      }
      offerToTakePayment(booking);
    } catch {
      refused();
    }
  }

  /**
   * The deposit, while they are still standing there.
   *
   * QA asked for a payment option on this screen. It is not a field on the
   * form, deliberately: a booking and a payment are two records, and a form
   * that writes both in one press can half-succeed — money recorded against a
   * booking that was refused, or a booking taken with a payment that silently
   * was not. So the booking is saved first, and *then* the same modal the
   * booking screen uses opens over the top of it. Reusing that modal is what
   * keeps VAT, back-dating and the overpayment guard identical wherever money
   * is taken.
   *
   * Closing it goes to the list either way. Taking an advance is the common
   * case, not a required one, and somebody who has nothing to record yet
   * presses Escape.
   */
  function offerToTakePayment(booking: {
    id: string;
    reference: string;
    customer_name?: string;
    total?: number;
  }): void {
    const owed = booking.total ?? 0;
    if (owed <= 0) {
      navigate('/bookings');
      return;
    }
    setTakePaymentFor({
      id: booking.id,
      reference: booking.reference,
      customerName: booking.customer_name ?? '',
      owed,
      isAdvance: true,
    });
  }

  /** The server said no. Make sure the reason is on screen. */
  function refused(): void {
    toast.problem('Not saved. The reason is on the form.');
    focusFirstProblem();
  }

  /**
   * Bring the first problem into view.
   *
   * A long form scrolls, and an error the reader cannot see is an error that
   * did not happen as far as they are concerned. Waits a frame so the message
   * being scrolled to has been rendered.
   */
  function focusFirstProblem(): void {
    requestAnimationFrame(() => {
      const target = formRef.current?.querySelector('[data-problem], [aria-invalid="true"]');
      if (target instanceof HTMLElement) {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        target.focus({ preventScroll: true });
      }
    });
  }

  if (isEditing && existing.isError) {
    return (
      <ErrorState
        headline="This booking did not load"
        detail="Go back to the bookings list and open it again."
        onRetry={() => void existing.refetch()}
        retryLabel="Load again"
      />
    );
  }

  const freeVehicles: readonly Vehicle[] = availability.data ?? [];
  const vehicleLabels = distinctVehicleNames(
    freeVehicles.map((vehicle) => ({
      id: vehicle.id,
      name: vehicle.display_name ?? `${vehicle.make} ${vehicle.model}`,
      plate: vehicle.plate,
    })),
  );

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-display font-semibold">
        {isEditing ? 'Edit booking' : fromEnquiry !== null ? 'Book this enquiry' : 'New booking'}
      </h1>

      <form
        ref={formRef}
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="flex flex-col gap-4">
          {/* 1. When ------------------------------------------------------- */}
          <Panel title="Time" note="Everything else follows from these" current>
            <PanelBody>
              <div className="flex flex-col gap-4">
                <FieldRow>
                  <Field label="Out" required error={errorFor('start_at')}>
                    {({ id: fieldId, describedBy }) => (
                      <DateTimeInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={startAt}
                        onChange={setStartAt}
                      />
                    )}
                  </Field>
                  {/* Checked as it is typed. Out could be set to next week and
                      Back to yesterday, and nothing said so until the database
                      refused it — with the customer still on the phone. */}
                  <Field
                    label="Back"
                    required
                    error={checkWindow(startAt, endAt) ?? errorFor('end_at')}
                  >
                    {({ id: fieldId, describedBy }) => (
                      <DateTimeInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={endAt}
                        onChange={setEndAt}
                      />
                    )}
                  </Field>
                </FieldRow>
              </div>
            </PanelBody>
          </Panel>

          {/* 2. Who --------------------------------------------------------
              One field, not two.

              It was a Search box and, below it, a Name box. You searched for
              "Ram", found nothing, and typed "Ram" again into the box
              underneath — the same six keystrokes twice, every new customer.
              There is one box now: what you type searches as you go, the
              matches appear under it, and if none of them is the right person
              that text *is* the new customer's name. Only the phone number is
              still asked for separately, because it is a different fact. */}
          {fromEnquiry === null && (
            <Panel title="Contact details">
              <PanelBody>
                <div className="flex flex-col gap-4">
                  {chosenCustomer !== undefined ? (
                    <div className="flex flex-wrap items-center justify-between gap-2 border-l-[3px] border-l-signal bg-signal-wash px-3 py-2">
                      <span>
                        Booking for <span className="font-semibold">{chosenCustomer.name}</span>
                        {(chosenCustomer.booking_count ?? 0) > 0 && (
                          <span className="text-ink-muted">
                            {' '}
                            — {String(chosenCustomer.booking_count)} previous{' '}
                            {chosenCustomer.booking_count === 1 ? 'booking' : 'bookings'}
                          </span>
                        )}
                      </span>
                      <Button
                        onClick={() => {
                          setCustomerId('');
                          setNewName(customerSearch);
                        }}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <FieldRow>
                      {/*
                        The suggestions belong to the Name box, so they live
                        inside its column and hang off it. They used to be a
                        sibling of the whole row, which made them the width of
                        the panel — a full-width bordered list under a
                        half-width field reads as another field, not as a list
                        of matches. Absolutely positioned so they float over
                        what is below rather than shoving the form down a line
                        on every keystroke.
                      */}
                      <div
                        className="relative"
                        onFocus={() => setBrowsing(true)}
                        onBlur={(event) => {
                          // Focus moving *within* this wrapper — from the box
                          // to one of the suggestions — is not leaving, so the
                          // list has to survive it. Anywhere else closes it.
                          if (!(event.relatedTarget instanceof Node)) {
                            setBrowsing(false);
                            return;
                          }
                          if (!event.currentTarget.contains(event.relatedTarget)) {
                            setBrowsing(false);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') setBrowsing(false);
                        }}
                      >
                        <Field
                          label="Name"
                          required
                          error={errorFor('name') ?? errorFor('customer')}
                        >
                          {({ id: fieldId, describedBy }) => (
                            <TextInput
                              id={fieldId}
                              describedBy={describedBy}
                              autoComplete="off"
                              value={customerSearch}
                              onChange={(value) => {
                                // Searching and naming are the same keystrokes.
                                setCustomerSearch(value);
                                setCustomerId('');
                                setNewName(value);
                                // Typing again after Escape, or after picking
                                // somebody and changing your mind, reopens it.
                                setBrowsing(true);
                              }}
                            />
                          )}
                        </Field>

                        {showSuggestions && (
                          <ul
                            role="listbox"
                            aria-label="Matching customers"
                            className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto divide-y divide-rule rounded-sm border border-rule-strong bg-panel shadow-lg"
                          >
                            {matches.map((customer) => (
                              <li key={customer.id} role="option" aria-selected={false}>
                                <button
                                  type="button"
                                  // The press has to survive losing focus.
                                  // Mousedown blurs the box before the click
                                  // lands, and not every browser focuses a
                                  // button on mousedown — so without this the
                                  // list can close out from under the finger
                                  // that is choosing from it.
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => {
                                    setCustomerId(customer.id);
                                    setNewPhone(customer.phone);
                                    setLicenceNo(customer.licence_no ?? '');
                                    setBrowsing(false);
                                  }}
                                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-signal-wash"
                                >
                                  <span className="min-w-0">
                                    <span className="block truncate">{customer.name}</span>
                                    {customer.is_blacklisted === true && (
                                      <span className="text-micro font-medium uppercase text-alert">
                                        Blacklisted
                                      </span>
                                    )}
                                  </span>
                                  <span className="identifier shrink-0 text-micro text-ink-muted">
                                    {customer.phone}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}

                        {/* Said plainly: an empty list otherwise reads as a
                            screen still loading. In the flow, not floating —
                            it is an answer, not a menu. */}
                        {browsing && searching && matches.length === 0 && !customers.isFetching && (
                          <p className="mt-1 text-small text-ink-muted">
                            Nobody on file matches that. It will be saved as a new customer.
                          </p>
                        )}
                      </div>

                      <Field label="Phone number" required error={errorFor('phone')}>
                        {({ id: fieldId, describedBy }) => (
                          <PhoneInput
                            id={fieldId}
                            describedBy={describedBy}
                            value={newPhone}
                            onChange={setNewPhone}
                          />
                        )}
                      </Field>
                    </FieldRow>
                  )}

                </div>
              </PanelBody>
            </Panel>
          )}

          {fromEnquiry !== null && enquiry.data !== undefined && (
            <Panel title="Contact details" note="From the enquiry">
              <PanelBody>
                <p className="text-body font-medium">{enquiry.data.name}</p>
                <p className="identifier text-ink-muted">{enquiry.data.phone}</p>
                {enquiry.data.vehicle_wanted && (
                  <p className="mt-2 text-ink-muted">Asked for: {enquiry.data.vehicle_wanted}</p>
                )}
              </PanelBody>
            </Panel>
          )}

          {/* 3. Where ------------------------------------------------------
              Before the vehicle, not after it, because it decides both which
              vehicles can be offered and which rate card prices the trip. It
              replaced a pair of free-text pickup and drop-off boxes that
              recorded two addresses — almost always the same office — and left
              the fact that actually changes the price written nowhere. */}
          <Panel title="Destination" note="This sets the rate, and which vehicles can go">
            <PanelBody>
              <FieldRow>
                <Field label="Area" required error={errorFor('area')}>
                  {({ id: fieldId, describedBy }) => (
                    <SelectInput
                      id={fieldId}
                      describedBy={describedBy}
                      value={area}
                      onChange={(value) => {
                        setArea(value);
                        // What was free for a valley trip is not necessarily
                        // free to leave it, so the answer on screen is stale
                        // the moment this changes.
                        setVehicleId('');
                        availability.reset();
                      }}
                      options={optionsFrom(TRIP_AREA_LABELS)}
                    />
                  )}
                </Field>
                <Field label="Going to" error={errorFor('destination')}>
                  {({ id: fieldId, describedBy }) => (
                    <TextInput
                      id={fieldId}
                      describedBy={describedBy}
                      value={destination}
                      onChange={setDestination}
                    />
                  )}
                </Field>
              </FieldRow>
            </PanelBody>
          </Panel>

          {/* 4. The vehicle -------------------------------------------------
              The list loads itself whenever the dates or the area change.
              There used to be a "Check what is free" button, which meant the
              commonest way to use this screen was to fill everything in, not
              press it, and find an empty dropdown. The question it answered —
              what is free — is one the form already knows enough to ask. */}
          <Panel title="Choose a vehicle" note="Only what is free for those times">
            <PanelBody>
              <div className="flex flex-col gap-4">
                {availability.isLoading ? (
                  <p className="text-ink-muted">Looking for free vehicles…</p>
                ) : freeVehicles.length === 0 ? (
                  <p className="border-l-[3px] border-l-attention bg-attention-wash px-3 py-2">
                    Nothing is free for those times. Change the dates, or the area.
                  </p>
                ) : (
                  <Field label="Vehicle" required error={errorFor('vehicle')}>
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={vehicleId}
                        onChange={setVehicleId}
                        options={[
                          { value: '', label: 'Choose one' },
                          ...freeVehicles.map((vehicle) => ({
                            value: vehicle.id,
                            label:
                              vehicleLabels.get(vehicle.id) ?? vehicle.display_name ?? vehicle.plate,
                          })),
                        ]}
                      />
                    )}
                  </Field>
                )}

                <Checkbox
                  label="With one of our drivers"
                  checked={withDriver}
                  onChange={setWithDriver}
                />

                {/* Self-drive means somebody is driving away in one of these
                    cars, so the licence is not optional — and it is the second
                    identity a blacklisting is matched on, because a phone
                    number is a SIM card away from being a different person. */}
                {/* The card is in a bag at the airport. Refusing the booking
                    loses it, and demanding a number on the spot gets a made-up
                    one typed in — which is worse, because it looks real. The
                    requirement moves to handover, where the customer is
                    standing there with the card. */}
                {!withDriver && (
                  <Checkbox
                    label="Licence number to follow — they will bring it at pickup"
                    checked={licenceToFollow}
                    onChange={(checked) => {
                      setLicenceToFollow(checked);
                      if (checked) setLicenceNo('');
                    }}
                  />
                )}

                {!withDriver && !licenceToFollow && (
                  <Field label="Licence number" required error={errorFor('licence_no')}>
                    {({ id: fieldId, describedBy }) => (
                      <TextInput
                        id={fieldId}
                        describedBy={describedBy}
                        identifier
                        value={licenceNo}
                        onChange={setLicenceNo}
                      />
                    )}
                  </Field>
                )}

                {withDriver && (
                  <Field label="Driver" error={errorFor('driver')}>
                    {({ id: fieldId, describedBy }) => (
                      <SelectInput
                        id={fieldId}
                        describedBy={describedBy}
                        value={driverId}
                        onChange={setDriverId}
                        options={[
                          { value: '', label: 'Assign later' },
                          ...(drivers.data?.results ?? []).map((driver) => ({
                            value: driver.id,
                            label: `${driver.name} — ${driver.phone}`,
                          })),
                        ]}
                      />
                    )}
                  </Field>
                )}
              </div>
            </PanelBody>
          </Panel>

          {/* 4. Money ------------------------------------------------------
              Shown whichever road you came in by. This panel used to be
              hidden when converting an enquiry, and the conversion priced the
              trip from the rate cards with no way to say otherwise — so a
              price haggled on the phone was silently replaced by the list
              price, and a trip with no matching rate card was booked at zero.
              A booking worth nothing then skipped the payment step entirely,
              because there was nothing to take. */}
          <Panel
              title="Price"
              note="From the rate cards. Change it if you agreed something else."
              actions={
                <Button onClick={() => void fetchQuote()} disabled={quoting.isLoading}>
                  {quoting.isLoading ? 'Working it out' : 'Quote from rates'}
                </Button>
              }
            >
              <PanelBody>
                <div className="flex flex-col gap-4">
                  {explanation !== '' && (
                    <p className="border-l-[3px] border-l-signal bg-signal-wash px-3 py-2 text-small">
                      {explanation}
                    </p>
                  )}

                  <FieldRow>
                    <Field label="Subtotal" error={errorFor('subtotal')}>
                      {({ id: fieldId, describedBy }) => (
                        <MoneyInput
                          id={fieldId}
                          describedBy={describedBy}
                          value={subtotal}
                          onChange={setSubtotal}
                        />
                      )}
                    </Field>
                    <Field label="Discount" error={errorFor('discount')}>
                      {({ id: fieldId, describedBy }) => (
                        <MoneyInput
                          id={fieldId}
                          describedBy={describedBy}
                          value={discount}
                          onChange={setDiscount}
                        />
                      )}
                    </Field>
                  </FieldRow>

                  <div className="flex items-baseline justify-between border-t border-rule pt-3">
                    <span className="font-medium">Total</span>
                    <span className="text-heading font-semibold">
                      <MoneyText amount={total} />
                    </span>
                  </div>
                </div>
              </PanelBody>
            </Panel>

          {/* 5. Details ----------------------------------------------------
              Also shown on both roads. Source is the exception: the enquiry
              already recorded how the customer got in touch, and that is a
              better answer than re-asking somebody who is reading it off the
              screen in front of them. */}
          <Panel title="Details">
              <PanelBody>
                <div className="flex flex-col gap-4">
                  <FieldRow>
                    <Field label="Fuel">
                      {({ id: fieldId, describedBy }) => (
                        <SelectInput
                          id={fieldId}
                          describedBy={describedBy}
                          value={fuelPolicy}
                          onChange={setFuelPolicy}
                          options={[...FUEL_POLICIES]}
                        />
                      )}
                    </Field>
                    {fromEnquiry === null && (
                      <Field label="Source">
                        {({ id: fieldId, describedBy }) => (
                          <SelectInput
                            id={fieldId}
                            describedBy={describedBy}
                            value={source}
                            onChange={setSource}
                            options={[...BOOKING_SOURCES]}
                          />
                        )}
                      </Field>
                    )}
                  </FieldRow>

                  <Field label="Notes">
                    {({ id: fieldId, describedBy }) => (
                      <textarea
                        id={fieldId}
                        aria-describedby={describedBy}
                        rows={2}
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                        className="w-full rounded-sm border border-rule-strong bg-panel px-2.5 py-2 text-body"
                      />
                    )}
                  </Field>
                </div>
              </PanelBody>
            </Panel>

          {/*
            Beside the button, not only beside the field.

            Whatever went wrong, something has to appear within a few
            centimetres of where the press happened, or the press looks
            ignored. `data-problem` is what focusFirstProblem scrolls to, and
            tabIndex makes it focusable so a screen reader lands on it too.
          */}
          {(problems.detail !== undefined || fieldProblems.length > 0) && (
            <div
              data-problem
              tabIndex={-1}
              role="alert"
              className="border border-alert/40 border-l-[3px] border-l-alert bg-alert-wash px-3 py-2.5 outline-none"
            >
              <p className="font-medium text-alert">
                {problems.detail ?? 'This booking was not saved. Fill in what is marked below.'}
              </p>
              {fieldProblems.length > 0 && (
                <ul className="mt-1 flex flex-col gap-0.5 text-small text-alert">
                  {fieldProblems.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            {/* Off until the form is right, which is the point of checking
                as it is typed: the press that does nothing is what made this
                screen feel broken. `title` says why for anyone who hovers. */}
            <Button
              intent="primary"
              size="touch"
              type="submit"
              disabled={saving || creatingCustomer.isLoading || !looksRight}
              title={looksRight ? undefined : 'Something above still needs filling in.'}
            >
              {saving ? 'Saving' : isEditing ? 'Save changes' : 'Take the booking'}
            </Button>
            <LinkButton to="/bookings" size="touch">
              Cancel
            </LinkButton>
          </div>
        </div>
      </form>

      {/* Opened by `offerToTakePayment` once the booking exists. Closing it —
          whether money was taken or not — is what leaves for the list. */}
      <PaymentModal
        booking={takePaymentFor}
        onClose={() => {
          setTakePaymentFor(null);
          navigate('/bookings');
        }}
      />
    </div>
  );
}
